import * as React from "react";
import { Avatar } from "../../../ui/components/Avatar";
import { getAdminUserProfile } from "../../lib/api/admin-users";
import { getServerOrganization } from "../../lib/api/auth";
import type { User } from "../../lib/api/users";
import { useI18n } from "../../lib/i18n";

/**
 * Resolves session/error rows' raw `userId`/`organizationId` to a display
 * name (+ avatar/logo) for the Product Analytics tables and detail pages.
 * There's no batch "get users/orgs by id list" endpoint anywhere in the
 * stack (checked user-service, pro-api, and pro-web), so this resolves one
 * id at a time via the existing single-lookup endpoints — deduped and
 * cached in module-level maps (shared across every mounted cell, not just
 * one component instance) so the same user/org appearing across many rows
 * or across pagination only round-trips once each per browser session.
 */

interface ResolvedOrg {
  name: string;
  logoUrl: string | null;
}

const userCache = new Map<string, User | null>();
const userInFlight = new Map<string, Promise<User | null>>();
const orgCache = new Map<string, ResolvedOrg | null>();
const orgInFlight = new Map<string, Promise<ResolvedOrg | null>>();

function resolveUser(accessToken: string, userId: string): Promise<User | null> {
  if (userCache.has(userId)) return Promise.resolve(userCache.get(userId)!);
  let promise = userInFlight.get(userId);
  if (!promise) {
    promise = getAdminUserProfile(accessToken, userId)
      .catch(() => null)
      .then((user) => {
        userCache.set(userId, user);
        userInFlight.delete(userId);
        return user;
      });
    userInFlight.set(userId, promise);
  }
  return promise;
}

function resolveOrganization(accessToken: string, organizationId: string): Promise<ResolvedOrg | null> {
  if (orgCache.has(organizationId)) return Promise.resolve(orgCache.get(organizationId)!);
  let promise = orgInFlight.get(organizationId);
  if (!promise) {
    promise = getServerOrganization(accessToken, organizationId)
      .then((raw) => ({
        name: String(raw.name ?? organizationId),
        logoUrl: raw.logoUrl != null ? String(raw.logoUrl) : raw.logo_url != null ? String(raw.logo_url) : null,
      }))
      .catch(() => null)
      .then((org) => {
        orgCache.set(organizationId, org);
        orgInFlight.delete(organizationId);
        return org;
      });
    orgInFlight.set(organizationId, promise);
  }
  return promise;
}

const cellRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
};

const cellTextStyle: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function OrgLogo({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: 6,
        border: "1px solid var(--color-border, #E5E7EB)",
        background: "var(--color-muted, #F3F4F6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--color-fgMuted, #9CA3AF)" }}>
          {name.charAt(0).toUpperCase() || "?"}
        </span>
      )}
    </div>
  );
}

/** Renders a session/error row's linked user as an avatar + name, resolved (and cached) client-side by id. */
export function UserCell({ userId, accessToken }: { userId: string | null; accessToken: string }) {
  const { t } = useI18n();
  const [user, setUser] = React.useState<User | null | undefined>(
    userId ? userCache.get(userId) : undefined,
  );

  React.useEffect(() => {
    if (!userId) return;
    if (userCache.has(userId)) {
      setUser(userCache.get(userId) ?? null);
      return;
    }
    let cancelled = false;
    void resolveUser(accessToken, userId).then((resolved) => {
      if (!cancelled) setUser(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, accessToken]);

  if (!userId) {
    return <span style={{ color: "var(--color-fgMuted)" }}>{t("pro.system.productAnalytics.session.anonymous")}</span>;
  }
  if (user === undefined) {
    return <span style={{ color: "var(--color-fgMuted)" }}>…</span>;
  }
  if (user === null) {
    // Resolvable id, but the lookup failed (deleted user, access denied, etc.) — fall back to the raw id.
    return <span style={{ color: "var(--color-fgMuted)", fontFamily: "monospace", fontSize: 12 }}>{userId}</span>;
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email;
  return (
    <div style={cellRowStyle}>
      <Avatar email={user.email} firstName={user.firstName} lastName={user.lastName} image={user.avatarUrl ?? undefined} size={24} />
      <span style={cellTextStyle} title={name}>
        {name}
      </span>
    </div>
  );
}

/** Renders a session/error row's linked organization as a logo + name, resolved (and cached) client-side by id. */
export function OrganizationCell({
  organizationId,
  accessToken,
}: {
  organizationId: string | null;
  accessToken: string;
}) {
  const [org, setOrg] = React.useState<ResolvedOrg | null | undefined>(
    organizationId ? orgCache.get(organizationId) : undefined,
  );

  React.useEffect(() => {
    if (!organizationId) return;
    if (orgCache.has(organizationId)) {
      setOrg(orgCache.get(organizationId) ?? null);
      return;
    }
    let cancelled = false;
    void resolveOrganization(accessToken, organizationId).then((resolved) => {
      if (!cancelled) setOrg(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, accessToken]);

  if (!organizationId) {
    return <span style={{ color: "var(--color-fgMuted)" }}>—</span>;
  }
  if (org === undefined) {
    return <span style={{ color: "var(--color-fgMuted)" }}>…</span>;
  }
  if (org === null) {
    return <span style={{ color: "var(--color-fgMuted)", fontFamily: "monospace", fontSize: 12 }}>{organizationId}</span>;
  }
  return (
    <div style={cellRowStyle}>
      <OrgLogo name={org.name} logoUrl={org.logoUrl} />
      <span style={cellTextStyle} title={org.name}>
        {org.name}
      </span>
    </div>
  );
}
